import { useEffect } from 'react';

import api from '@mint-lib/api';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { keepPreviousData, useQuery } from '@mint-lib/query-client';
import { getRouteApi } from '@mint/routing';

import { queryClient } from '../api/queries/client.js';
import { PREDICTIVE_ERROR } from '../constants/campaignBuilder.js';
import { GET_PREDICTIVE_PLANING } from '../constants/EndpointConstants.js';
import { QueryKey } from '../constants/queryKeys.js';
import {
  GetPredictivePlanningPayload,
  GetPredictivePlanningResponseSchema,
  type GetPredictivePlanningResponse,
} from '../types/predictivePlanning.js';
import { getNestedValue, type NestedKeyOf } from '../utils/helpers.js';
import { useGetBudgetRows, usePredictiveBudget } from './useBudgetRows.js';
import { useBuyingPlanInfoSelector } from './useGetBuyingPlanData.js';
import { useSelectedKPI } from './usePredictivePlanningMetrics.js';
import { usePredictivePlanningMode } from './usePredictivePlanningMode.js';

const getPredictivePlanningData = async (
  payload: GetPredictivePlanningPayload,
) => {
  const response = await api.post(GET_PREDICTIVE_PLANING, payload);
  return GetPredictivePlanningResponseSchema.parse(response.data);
};
const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

export const useGetPredictivePlanningData = () => {
  const { t } = useTranslation('@myn/planning');

  const { campaignId } = routeApi.useParams();
  const selectedPlan = useBuyingPlanInfoSelector();
  const [budgetRows] = useGetBudgetRows();
  const [predictiveBudget] = usePredictiveBudget();
  const [selectedKPI] = useSelectedKPI();

  const payload = {
    totalBudget: Number(predictiveBudget),
    targetKpi: selectedKPI ?? '',
    clientId: selectedPlan ? String(selectedPlan.company) : '',
    journeyId: campaignId,
    flightStartDate: selectedPlan?.dateFrom ?? '',
    flightEndDate: selectedPlan?.dateTo ?? '',
    mediaRows: budgetRows ?? {},
  };

  const { isFetching, isSuccess, refetch, data } = useQuery({
    queryKey: [QueryKey.GET_PREDICTIVE_PLANNING_ROWS, payload],
    queryFn: () => getPredictivePlanningData(payload),
    enabled: false,
  });

  useEffect(() => {
    const isInfeasibleErr =
      data?.optimizationStatus?.error === PREDICTIVE_ERROR.INFEASIBLE;

    const hasError =
      Boolean(data?.optimizationStatus?.error) &&
      data?.optimizationStatus?.status === 0;

    if (!isFetching && (isInfeasibleErr || hasError)) {
      enqueueSnackbar({
        variant: 'warning',
        title: t("Predictive Planning couldn't generate suggestions."),
        subTitle: t(
          'Check your budget selections and ensure they align with media row budgets and constraints before retrying',
        ),
        autoHideDuration: 6000,
        removable: true,
      });
    }
  }, [isFetching, isSuccess, data, t]);

  return {
    isSimulationLoading: isFetching,
    isSimulationSuccess: isSuccess,
    fetchPredictivePlanningData: refetch,
  };
};

export const resetPredictivePlanningQueryData = () => {
  queryClient.resetQueries({
    queryKey: [QueryKey.GET_PREDICTIVE_PLANNING_ROWS],
  });
};

export const usePredictiveDataSelector = <
  K extends NestedKeyOf<GetPredictivePlanningResponse>,
>(
  path: K,
) => {
  const { campaignId } = routeApi.useParams();
  const selectedPlan = useBuyingPlanInfoSelector();
  const [budgetRows] = useGetBudgetRows();
  const [predictiveBudget] = usePredictiveBudget();
  const [selectedKPI] = useSelectedKPI();
  const isPredictivePlanningMode = usePredictivePlanningMode();

  const payload = {
    totalBudget: Number(predictiveBudget),
    targetKpi: selectedKPI ?? '',
    clientId: selectedPlan ? String(selectedPlan.company) : '',
    journeyId: campaignId,
    flightStartDate: selectedPlan?.dateFrom ?? '',
    flightEndDate: selectedPlan?.dateTo ?? '',
    mediaRows: budgetRows ?? {},
  };

  const { data } = useQuery({
    queryKey: [QueryKey.GET_PREDICTIVE_PLANNING_ROWS, payload],
    queryFn: () => getPredictivePlanningData(payload),
    enabled: false,
    placeholderData: isPredictivePlanningMode ? keepPreviousData : undefined,
    select: (data) => getNestedValue(data, path),
  });

  return { data };
};
